import React, { useEffect, useId, useState } from "react";
import { createPortal } from "react-dom";
import { Loader2, X } from "lucide-react";

const RequestConfirmDialog = ({
  open,
  title,
  body,
  details = [],
  amountHighlight,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  tone = "primary",
  destructive = false,
  busy = false,
  requireConsent = false,
  consentLabel,
  onClose,
  onConfirm,
}) => {
  const titleId = useId();
  const [consented, setConsented] = useState(false);

  useEffect(() => {
    if (open) setConsented(false);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;

    const onKeyDown = (event) => {
      if (event.key === "Escape" && !busy) onClose?.();
    };

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [open, busy, onClose]);

  if (!open) return null;

  const confirmDisabled = busy || (requireConsent && !consented);
  const confirmClass = destructive
    ? "req-btn req-btn-danger"
    : tone === "success"
      ? "req-btn req-btn-success"
      : "req-btn req-btn-primary";

  return createPortal(
    <div className="req-dialog-root" role="presentation">
      <button
        type="button"
        className="req-dialog-backdrop"
        aria-label="Close dialog"
        disabled={busy}
        onClick={() => {
          if (!busy) onClose?.();
        }}
      />
      <div
        className="req-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className="req-dialog-close"
          aria-label="Close"
          onClick={onClose}
          disabled={busy}
        >
          <X size={16} />
        </button>

        <h2 id={titleId}>{title}</h2>
        {body ? <p className="req-dialog-lead">{body}</p> : null}

        {amountHighlight ? (
          <div className="req-dialog-amount">
            <em>{amountHighlight.label}</em>
            <strong>{amountHighlight.value}</strong>
          </div>
        ) : null}

        {details.length ? (
          <dl className="req-dialog-details">
            {details.map((row) => (
              <div key={row.label}>
                <dt>{row.label}</dt>
                <dd>{row.value}</dd>
              </div>
            ))}
          </dl>
        ) : null}

        {requireConsent && consentLabel ? (
          <label className={`req-dialog-consent ${consented ? "req-dialog-consent-active" : ""}`}>
            <input
              type="checkbox"
              checked={consented}
              disabled={busy}
              onChange={(e) => setConsented(e.target.checked)}
            />
            <span>{consentLabel}</span>
          </label>
        ) : null}

        <div className="req-dialog-actions">
          <button
            type="button"
            className="req-btn req-btn-ghost"
            onClick={onClose}
            disabled={busy}
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            className={confirmClass}
            onClick={onConfirm}
            disabled={confirmDisabled}
          >
            {busy ? <Loader2 size={15} className="animate-spin" /> : null}
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default RequestConfirmDialog;
